import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { buildReport, exportReportExcel, getReportFields } from "../../../api/financeApi";
import "./Reports.css";

const REPORTS = [
  { id: "opiu", label: "ОПиУ" },
  { id: "dds", label: "ДДС" },
  { id: "balance", label: "Баланс" },
  { id: "universal", label: "Универсальный" },
];

const UNITS = [
  { id: "tenge", label: "тенге" },
  { id: "thousand", label: "тыс." },
  { id: "million", label: "млн" },
  { id: "billion", label: "млрд" },
];

const ZERO_MODES = [
  { id: "none", label: "не скрывать" },
  { id: "rows", label: "пустые строки" },
  { id: "columns", label: "пустые колонки" },
  { id: "both", label: "строки и колонки" },
];

const emptyRequest = () => ({
  dateFrom: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd"),
  dateTo: format(new Date(), "yyyy-MM-dd"),
  organization: "",
  department: "",
  rows: [],
  columns: [],
  excludeVgo: false,
  includeAllocation: true,
  dataGroup: "fact",
  unit: "thousand",
  suppressZero: "rows",
});

const buildBody = (report, form) => {
  const body = {
    date_from: form.dateFrom,
    date_to: form.dateTo,
    filters: {
      ...(form.organization.trim()
        ? { organization: form.organization.split(",").map((item) => item.trim()).filter(Boolean) }
        : {}),
      ...(form.department.trim()
        ? { "project.department": form.department.split(",").map((item) => item.trim()).filter(Boolean) }
        : {}),
    },
    columns: form.columns,
    exclude_vgo: form.excludeVgo,
    include_allocation: form.includeAllocation,
    data_group: form.dataGroup,
    unit: form.unit,
    suppress_zero: form.suppressZero,
    compare_date_from: null,
    compare_date_to: null,
  };
  if (report === "universal") body.rows = form.rows;
  return body;
};

function Reports() {
  const [report, setReport] = useState("opiu");
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(emptyRequest);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    getReportFields(controller.signal)
      .then((items) => setFields(Array.isArray(items) ? items : []))
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError(requestError.message);
      });
    return () => controller.abort();
  }, []);

  const rowFields = useMemo(
    () => fields.filter((field) => field.kind !== "column"),
    [fields]
  );
  const columnFields = useMemo(
    () => fields.filter((field) => field.kind !== "row"),
    [fields]
  );

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const toggle = (key, value) => {
    setForm((prev) => {
      const selected = new Set(prev[key]);
      if (selected.has(value)) selected.delete(value);
      else selected.add(value);
      return { ...prev, [key]: [...selected] };
    });
  };

  const run = async (asExcel) => {
    setLoading(true);
    setError(null);
    try {
      const body = buildBody(report, form);
      if (asExcel) await exportReportExcel(report, body);
      else setResult(await buildReport(report, body));
    } catch (requestError) {
      setError(requestError.message || "Не удалось построить отчёт");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="finances-reports">
      <div className="fin-panel">
        <div className="fin-panel__row">
          {REPORTS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`fin-chip${report === item.id ? " is-active" : ""}`}
              onClick={() => setReport(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="fin-grid">
          <label>
            С
            <input type="date" value={form.dateFrom} onChange={(event) => set({ dateFrom: event.target.value })} />
          </label>
          <label>
            По
            <input type="date" value={form.dateTo} onChange={(event) => set({ dateTo: event.target.value })} />
          </label>
          <label>
            Организация
            <input
              value={form.organization}
              placeholder="AQMOL-proBIM ТОО"
              onChange={(event) => set({ organization: event.target.value })}
            />
          </label>
          <label>
            Отделение
            <input
              value={form.department}
              placeholder="Отделение РП МЖК (общие)"
              onChange={(event) => set({ department: event.target.value })}
            />
          </label>
          <label>
            Единицы
            <select value={form.unit} onChange={(event) => set({ unit: event.target.value })}>
              {UNITS.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          <label>
            Данные
            <select value={form.dataGroup} onChange={(event) => set({ dataGroup: event.target.value })}>
              <option value="fact">Факт</option>
              <option value="plan">План</option>
              <option value="forecast">Прогноз</option>
            </select>
          </label>
          <label>
            Нулевые
            <select value={form.suppressZero} onChange={(event) => set({ suppressZero: event.target.value })}>
              {ZERO_MODES.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="fin-checks">
          <label>
            <input
              type="checkbox"
              checked={form.excludeVgo}
              onChange={(event) => set({ excludeVgo: event.target.checked })}
            />
            Исключить ВГО
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.includeAllocation}
              onChange={(event) => set({ includeAllocation: event.target.checked })}
            />
            Включать распределение
          </label>
        </div>

        <div className="fin-fields">
          {report === "universal" && (
          <div>
            <div className="fin-fields__title">Строки</div>
            {rowFields.map((field) => (
              <label key={field.key}>
                <input
                  type="checkbox"
                  checked={form.rows.includes(field.key)}
                  onChange={() => toggle("rows", field.key)}
                />
                {field.title || field.key}
              </label>
            ))}
          </div>
          )}
          <div>
            <div className="fin-fields__title">Колонки</div>
            {columnFields.map((field) => (
              <label key={`col-${field.key}`}>
                <input
                  type="checkbox"
                  checked={form.columns.includes(field.key)}
                  onChange={() => toggle("columns", field.key)}
                />
                {field.title || field.key}
              </label>
            ))}
          </div>
        </div>

        <div className="fin-actions">
          <button type="button" className="fin-btn fin-btn--primary" disabled={loading} onClick={() => run(false)}>
            {loading ? "Считаю..." : "Построить"}
          </button>
          <button type="button" className="fin-btn" disabled={loading} onClick={() => run(true)}>
            Excel
          </button>
        </div>
        {error && <div className="fin-error">{error}</div>}
      </div>

      {result && (
        <div className="fin-panel">
          <h2 className="fin-title">{result.title}</h2>
          <div className="fin-table-wrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Показатель</th>
                  {(result.columns || []).map((column) => (
                    <th key={column.key}>{column.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(result.rows || []).map((row) => (
                  <tr key={row.id}>
                    <td style={{ paddingLeft: 12 + row.level * 16 }}>{row.title}</td>
                    {(result.columns || []).map((column) => (
                      <td key={column.key} className="is-num">
                        {row.values?.[column.key] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;
